import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  costChangeHistoryApi,
  createFutureCostApi,
  deleteFutureCostApi,
  downloadFutureCostTemplateApi,
  futureCostsApi,
  importFutureCostApi,
  updateFutureCostApi,
} from "../apis/futureCostApis";
import { downloadFile, errorMessage, successMessage } from "../../../utils";
import { useNavigate } from "react-router-dom";

const queryKeys = {
  futureCostList: (params) => ["future-cost-list", params],
  costChangeHistoryList: (id, params) => [
    "future-cost-change-history",
    id,
    params,
  ],
};

export const useFutureCostQueries = ({
  ingredientId = null,
  list = false,
  costChangeHistory = false,
  getListParamsCallback = null,
  getHistoryListParamsCallback = null,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params =
    typeof getListParamsCallback === "function" ? getListParamsCallback() : {};
  const costHistoryParams =
    typeof getHistoryListParamsCallback === "function"
      ? getHistoryListParamsCallback()
      : {};

  const listQuery = useQuery({
    queryFn: () => futureCostsApi(params),
    queryKey: queryKeys.futureCostList(params),
    select: (response) => response?.data || [],
    enabled: list,
  });

  const costChangeHistoryQuery = useQuery({
    queryFn: () => costChangeHistoryApi(ingredientId, costHistoryParams),
    queryKey: queryKeys.costChangeHistoryList(ingredientId, costHistoryParams),
    select: (response) => response?.data || [],
    enabled: costChangeHistory && !!ingredientId,
  });

  const createFutureQuery = useMutation({
    mutationFn: createFutureCostApi,
    onSuccess: ({ success, message }) => {
      if (success) {
        successMessage(message);
        navigate("/future-cost");
      } else {
        errorMessage(message);
      }
    },
    onError: (error) =>
      errorMessage(error.response.data?.message || "Cost creation failed"),
  });

  const updateFutureQuery = useMutation({
    mutationFn: updateFutureCostApi,
    onSuccess: ({ success, message }) => {
      success ? successMessage(message) : errorMessage(message);
      queryClient.invalidateQueries({
        queryKey: queryKeys.futureCostList(params),
      });
    },
    onError: (error) =>
      errorMessage(error.response.data?.message || "Cost update failed"),
  });

  const deleteFutureCostQuery = useMutation({
    mutationFn: deleteFutureCostApi,
    onSuccess: ({ success, message }) => {
      success ? successMessage(message) : errorMessage(message);

      queryClient.invalidateQueries({
        queryKey: queryKeys.futureCostList(params),
      });
    },
    onError: (error) =>
      errorMessage(error.response.data?.message || "Cost deletion failed"),
  });

  const downloadTemplateQuery = useMutation({
    mutationFn: downloadFutureCostTemplateApi,
    onError: (error) => {
      errorMessage(error?.response?.data?.message || "Download failed");
    },
    onSuccess: (response) =>
      downloadFile({ response, fileName: "future-cost-template" }),
  });

  const importTemplateQuery = useMutation({
    mutationFn: importFutureCostApi,
    onSuccess: (response) => {
      response?.success
        ? successMessage(response.message)
        : errorMessage(response?.message || "Import Filed");

      return response;
    },
    onError: (error) => {
      if (error?.response?.status === 422) {
        errorMessage(
          error.response.data?.message || "Only Excel files are allowed",
        );
      } else {
        errorMessage("Upload failed");
      }
    },
  });

  return {
    listQuery,
    createFutureQuery,
    updateFutureQuery,
    deleteFutureCostQuery,
    costChangeHistoryQuery,
    downloadTemplateQuery,
    importTemplateQuery,
  };
};
